import { useEffect, useState } from 'react';
import { storageAPI } from '../lib/api';
import {
  HardDrive, Image as ImageIcon, FileVideo, FileText, RefreshCw, AlertTriangle,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid,
} from 'recharts';

const formatSize = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const TYPE_META = {
  photo: { label: 'Fotos', color: '#06b6d4', icon: ImageIcon },
  video: { label: 'Vídeos', color: '#a855f7', icon: FileVideo },
  document: { label: 'Documentos', color: '#f59e0b', icon: FileText },
};

/**
 * Storage usage widget. Pass `projectId` to filter to one project, or omit to
 * aggregate across all projects the user has access to.
 *
 * Exposed externally via `refreshKey` prop change to trigger a reload.
 */
export default function StorageWidget({ projectId, refreshKey = 0, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await storageAPI.usage(projectId || undefined);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId, refreshKey]);

  if (loading && !data) {
    return (
      <div className="card-hud p-4 animate-pulse" data-testid="storage-widget-loading">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div className="h-2 bg-gray-200 rounded w-full mb-4"></div>
        <div className="h-32 bg-gray-100 rounded"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="card-hud p-4 border-red-300" data-testid="storage-widget-error">
        <div className="flex items-center gap-2 text-red-600 text-xs">
          <AlertTriangle size={14} />
          {error}
          <button onClick={load} className="ml-auto text-gold hover:underline">tentar novamente</button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const totalBytes = data.total_bytes || 0;
  const quotaBytes = data.quota_bytes || 1;
  const quotaPct = data.quota_pct || 0;
  const types = Object.entries(data.by_type || {}).map(([k, v]) => ({
    key: k,
    name: TYPE_META[k]?.label || k,
    color: TYPE_META[k]?.color || '#94a3b8',
    bytes: v.bytes || 0,
    count: v.count || 0,
  }));
  const chartData = types.filter(t => t.bytes > 0).map(t => ({
    name: t.name,
    bytes: t.bytes,
    fill: t.color,
    count: t.count,
  }));
  const maxBytes = Math.max(1, ...chartData.map(d => d.bytes));

  // Bar color logic for the quota progress bar
  const barColor =
    quotaPct >= 90 ? 'bg-red-500'
      : quotaPct >= 70 ? 'bg-amber-500'
      : 'bg-cyan-500';

  return (
    <div className="card-hud overflow-hidden" data-testid="storage-widget">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-cyan-50 to-transparent">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-cyan-500/15 text-cyan-600 flex items-center justify-center">
            <HardDrive size={14} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-cyan-700 font-bold">
              ARMAZENAMENTO EM NUVEM
            </div>
            <div className="text-[10px] text-gray-500 -mt-0.5">
              {data.total_files.toLocaleString('pt-BR')} arquivo(s) · {formatSize(totalBytes)} usado(s)
            </div>
          </div>
        </div>
        <button
          onClick={load}
          className="text-gray-400 hover:text-cyan-600 transition-colors p-1.5"
          title="Atualizar"
          data-testid="storage-widget-refresh"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Quota bar */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="font-medium text-gray-600">
            {formatSize(totalBytes)} <span className="text-gray-400">de</span> {formatSize(quotaBytes)}
          </span>
          <span className={`font-mono font-bold ${quotaPct >= 90 ? 'text-red-600' : quotaPct >= 70 ? 'text-amber-600' : 'text-cyan-600'}`} data-testid="storage-quota-pct">
            {quotaPct.toFixed(1)}%
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all duration-500`}
            style={{ width: `${Math.min(100, quotaPct)}%` }}
            data-testid="storage-quota-bar"
          />
        </div>
      </div>

      {!compact && (
        <>
          {/* Type breakdown - cards */}
          <div className="px-4 pb-3 grid grid-cols-3 gap-2">
            {types.map((t) => {
              const Icon = TYPE_META[t.key]?.icon || FileText;
              const pct = totalBytes > 0 ? (t.bytes / totalBytes * 100) : 0;
              return (
                <div
                  key={t.key}
                  className="rounded-lg border border-gray-100 bg-gray-50/50 px-2.5 py-2"
                  data-testid={`storage-type-${t.key}`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon size={11} style={{ color: t.color }} />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
                      {t.name}
                    </span>
                  </div>
                  <div className="text-sm font-bold text-gray-800 leading-tight">
                    {formatSize(t.bytes)}
                  </div>
                  <div className="text-[10px] text-gray-400 leading-tight">
                    {t.count.toLocaleString('pt-BR')} arquivo(s) · {pct.toFixed(0)}%
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bar chart by type */}
          {chartData.length > 0 && (
            <div className="px-2 pb-2" data-testid="storage-type-chart">
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    axisLine={{ stroke: '#d1d5db' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    tickFormatter={(v) => formatSize(v)}
                    domain={[0, maxBytes]}
                    axisLine={{ stroke: '#d1d5db' }}
                    tickLine={false}
                    width={56}
                  />
                  <Tooltip
                    formatter={(value, name) => [formatSize(value), 'Tamanho']}
                    labelStyle={{ fontSize: 11, fontWeight: 600 }}
                    contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="bytes" radius={[6, 6, 0, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={`cell-${i}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top projects (only when no project filter) */}
          {!projectId && data.by_project && data.by_project.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/40" data-testid="storage-top-projects">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">
                TOP PROJETOS
              </div>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {data.by_project.slice(0, 5).map((p) => {
                  const pct = totalBytes > 0 ? (p.bytes / totalBytes * 100) : 0;
                  return (
                    <div key={p.project_id} className="flex items-center gap-2 text-[11px]">
                      <div className="flex-1 truncate text-gray-700">{p.project_name}</div>
                      <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-cyan-500" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-16 text-right font-mono text-gray-600">{formatSize(p.bytes)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
